import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { useWorkspaceContext } from '@/contexts/WorkspaceContext';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { useChat } from '@/contexts/ChatContext';
import {
  uberDirectApi,
  type UberDirectEnvironment,
  type UberDirectEndpoint,
  type UberManifestItem,
  type UberDeliveryDetail,
  type UberProofOfDelivery,
  type UberProofWaypoint,
  type UberProofType,
} from '@/lib/api/services/uberDirect';
import {
  Loader2, PlugZap, Unplug, ArrowLeft, CheckCircle, XCircle, Truck,
  ExternalLink, RefreshCw, Ban, Plus, Trash2, Clock, MapPin,
  Eye, FileCheck, Copy, Webhook, Package, User, Phone, List, Lock, Radio,
  Navigation, Circle,
} from 'lucide-react';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { AnimatedTabs } from '@/components/reusable/AnimatedTabs';
import { PaginationFooter } from '@/components/admin/external/PaginationFooter';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import MapLocationPicker, { type PickedAddress } from '@/components/location/MapLocationPicker';
import { GOOGLE_MAPS_ENABLED } from '@/lib/maps/config';
import { showApiError } from '@/lib/errors';
import { DisconnectConfirmDialog } from '@/components/integrations/DisconnectConfirmDialog';

const MAPS_ENABLED = GOOGLE_MAPS_ENABLED;

const NON_CANCELABLE_STATUSES = new Set([
  'complete', 'pickup_complete', 'dropoff', 'delivered', 'canceled', 'cancelled', 'returned',
]);

const PROOF_STATUSES = new Set(['delivered', 'complete', 'returned']);

const ACTIVE_STATUSES = new Set(['pending', 'pickup', 'pickup_complete', 'dropoff']);
const DELIVERED_STATUSES = new Set(['delivered', 'complete']);
const CLOSED_STATUSES = new Set(['canceled', 'cancelled', 'returned']);

const STATUS_FILTER_OPTIONS = [
  'all', 'quote', 'pending', 'pickup', 'pickup_complete', 'dropoff',
  'delivered', 'canceled', 'returned',
] as const;

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

const formatFee = (fee?: number | null, currency?: string | null) => {
  if (fee === undefined || fee === null || Number.isNaN(Number(fee))) return '—';
  const amount = Number(fee) / 100;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (currency || 'USD').toUpperCase(),
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${(currency || '').toUpperCase()}`.trim();
  }
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
};

const composeStructuredAddress = (raw?: Record<string, unknown> | null): string => {
  if (!raw || typeof raw !== 'object') return '';
  const a = raw as Record<string, unknown>;
  let street = '';
  if (Array.isArray(a.street_address)) {
    street = (a.street_address as unknown[]).filter(Boolean).map(String).join(', ');
  } else if (typeof a.street_address === 'string') {
    street = a.street_address;
  } else {
    street = [a.street_address_1, a.street_address_2]
      .map((p) => (p == null ? '' : String(p)))
      .filter(Boolean)
      .join(', ');
  }
  const parts = [street, a.city, a.state, a.zip_code, a.country]
    .map((p) => (p == null ? '' : String(p).trim()))
    .filter(Boolean);
  return parts.join(', ');
};

const formatWaypointAddress = (
  address?: string | Record<string, unknown> | null,
  detailedAddress?: Record<string, unknown> | null
): string => {
  try {
    if (typeof address === 'string' && address.trim()) return address.trim();
    if (address && typeof address === 'object') {
      const a = address as Record<string, unknown>;
      const inner = a.address && typeof a.address === 'object'
        ? composeStructuredAddress(a.address as Record<string, unknown>)
        : '';
      const composed = composeStructuredAddress(a);
      if (inner) return inner;
      if (composed) return composed;
    }
    const fromDetailed = composeStructuredAddress(detailedAddress);
    if (fromDetailed) return fromDetailed;
  } catch {
  }
  return '—';
};

const parseStoredAddress = (raw?: string | null): string => {
  if (!raw || typeof raw !== 'string') return '—';
  const trimmed = raw.trim();
  if (!trimmed) return '—';
  try {
    const parsed = JSON.parse(trimmed);
    if (typeof parsed === 'string') return parsed.trim() || '—';
    if (parsed && typeof parsed === 'object') {
      const inner = (parsed as Record<string, unknown>).address;
      const composed = inner && typeof inner === 'object'
        ? composeStructuredAddress(inner as Record<string, unknown>)
        : composeStructuredAddress(parsed as Record<string, unknown>);
      return composed || '—';
    }
    return '—';
  } catch {
    return trimmed;
  }
};

const formatLatLng = (loc?: { lat?: number; lng?: number } | null): string => {
  const lat = loc?.lat;
  const lng = loc?.lng;
  if (typeof lat !== 'number' || typeof lng !== 'number' || Number.isNaN(lat) || Number.isNaN(lng)) {
    return '';
  }
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
};

const formatCourierVehicle = (courier?: {
  vehicle?: { make?: string; model?: string; color?: string; type?: string } | null;
  vehicle_make?: string;
  vehicle_model?: string;
  vehicle_color?: string;
  vehicle_type?: string;
} | null): string => {
  if (!courier) return '';
  const v = courier.vehicle || {};
  const color = v.color || courier.vehicle_color;
  const make = v.make || courier.vehicle_make;
  const model = v.model || courier.vehicle_model;
  const type = v.type || courier.vehicle_type;
  const desc = [color, make, model].filter(Boolean).join(' ');
  if (desc && type) return `${desc} · ${type}`;
  return desc || type || '';
};

const TIMELINE_STEPS: { key: string; label: string; matches: string[] }[] = [
  { key: 'pending', label: 'Pending', matches: ['pending'] },
  { key: 'pickup', label: 'En route to pickup', matches: ['pickup'] },
  { key: 'pickup_complete', label: 'Picked up', matches: ['pickup_complete'] },
  { key: 'dropoff', label: 'Out for dropoff', matches: ['dropoff'] },
  { key: 'delivered', label: 'Delivered', matches: ['delivered', 'complete'] },
];

const TERMINAL_FAILURE_STATUSES = new Set(['canceled', 'cancelled', 'returned']);

const DeliveryTimeline: React.FC<{ status?: string }> = ({ status }) => {
  const s = (status || '').toLowerCase();

  if (TERMINAL_FAILURE_STATUSES.has(s)) {
    const isReturned = s === 'returned';
    return (
      <div className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm ${isReturned ? 'bg-orange-50 text-orange-700' : 'bg-red-50 text-red-700'}`}>
        <XCircle className="h-4 w-4 shrink-0" />
        <span className="font-medium">{isReturned ? 'Returned to sender' : 'Canceled'}</span>
        <span className="text-xs opacity-80">— this delivery did not complete.</span>
      </div>
    );
  }

  let currentIdx = TIMELINE_STEPS.findIndex((step) => step.matches.includes(s));
  if (currentIdx < 0) currentIdx = 0;

  return (
    <ol className="flex flex-col gap-0 sm:flex-row sm:items-start sm:gap-0">
      {TIMELINE_STEPS.map((step, idx) => {
        const done = idx < currentIdx;
        const active = idx === currentIdx;
        const StepIcon = done ? CheckCircle : active ? Navigation : Circle;
        const color = done
          ? 'text-green-600'
          : active
            ? 'text-blue-600'
            : 'text-muted-foreground/40';
        return (
          <li key={step.key} className="flex flex-1 items-center gap-2 sm:flex-col sm:gap-1 sm:text-center">
            <div className="flex items-center sm:w-full">
              {idx > 0 && (
                <span className={`hidden h-0.5 flex-1 sm:block ${idx <= currentIdx ? 'bg-green-500/60' : 'bg-muted-foreground/20'}`} />
              )}
              <StepIcon className={`h-4 w-4 shrink-0 ${color} ${active ? 'animate-pulse' : ''}`} />
              {idx < TIMELINE_STEPS.length - 1 && (
                <span className={`hidden h-0.5 flex-1 sm:block ${idx < currentIdx ? 'bg-green-500/60' : 'bg-muted-foreground/20'}`} />
              )}
            </div>
            <span className={`text-xs ${active ? 'font-semibold text-foreground' : done ? 'text-foreground' : 'text-muted-foreground'}`}>
              {step.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
};

const emptyEndpoint = (): EndpointForm => ({
  street1: '', street2: '', city: '', state: '', zip_code: '', country: '',
  name: '', phone_number: '', latitude: '', longitude: '',
});

interface EndpointForm {
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  name: string;
  phone_number: string;
  latitude: string;
  longitude: string;
}

const buildEndpoint = (f: EndpointForm): UberDirectEndpoint => {
  const street_address = [f.street1.trim(), f.street2.trim()].filter(Boolean);
  const endpoint: UberDirectEndpoint = {
    address: {
      street_address,
      city: f.city.trim(),
      state: f.state.trim(),
      zip_code: f.zip_code.trim(),
      country: f.country.trim() || 'US',
    },
  };
  if (f.name.trim()) endpoint.name = f.name.trim();
  if (f.phone_number.trim()) endpoint.phone_number = f.phone_number.trim();
  if (f.latitude.trim() && !Number.isNaN(Number(f.latitude))) endpoint.latitude = Number(f.latitude);
  if (f.longitude.trim() && !Number.isNaN(Number(f.longitude))) endpoint.longitude = Number(f.longitude);
  return endpoint;
};

const endpointValid = (f: EndpointForm) =>
  Boolean(f.street1.trim() && f.city.trim() && f.state.trim() && f.zip_code.trim() && f.country.trim());

interface DeliveryRow {
  id?: string;
  delivery_id?: string;
  quote_id?: string;
  external_id?: string;
  status?: string;
  complete?: boolean;
  fee?: number;
  currency?: string;
  tracking_url?: string;
  dropoff_name?: string;
  pickup_name?: string;
  pickup_address?: string;
  dropoff_address?: string;
  courier_name?: string;
  courier_phone?: string;
  pickup_eta?: string;
  dropoff_eta?: string;
  undeliverable_reason?: string;
  created_at?: string;
  updated_at?: string;
}

const getStatusBadge = (status?: string) => {
  const s = (status || '').toLowerCase();
  const map: Record<string, { label: string; className: string; icon: React.ElementType }> = {
    quote: { label: 'Quote', className: 'bg-slate-100 text-slate-700', icon: Clock },
    pending: { label: 'Pending', className: 'bg-amber-100 text-amber-700', icon: Clock },
    pickup: { label: 'Pickup', className: 'bg-blue-100 text-blue-700', icon: Truck },
    pickup_complete: { label: 'Picked Up', className: 'bg-indigo-100 text-indigo-700', icon: Truck },
    dropoff: { label: 'Dropoff', className: 'bg-violet-100 text-violet-700', icon: Truck },
    delivered: { label: 'Delivered', className: 'bg-green-100 text-green-700', icon: CheckCircle },
    complete: { label: 'Complete', className: 'bg-green-100 text-green-700', icon: CheckCircle },
    canceled: { label: 'Canceled', className: 'bg-red-100 text-red-700', icon: XCircle },
    cancelled: { label: 'Canceled', className: 'bg-red-100 text-red-700', icon: XCircle },
    returned: { label: 'Returned', className: 'bg-orange-100 text-orange-700', icon: XCircle },
  };
  const info = map[s] || { label: status || 'Unknown', className: 'bg-slate-100 text-slate-700', icon: Clock };
  const Icon = info.icon;
  return (
    <Badge variant="outline" className={`flex items-center w-fit gap-1 border-0 ${info.className}`}>
      <Icon className="h-3 w-3" />
      {info.label}
    </Badge>
  );
};

const EndpointFields: React.FC<{
  title: string;
  value: EndpointForm;
  onChange: (next: EndpointForm) => void;
  idPrefix: string;
}> = ({ title, value, onChange, idPrefix }) => {
  const set = (k: keyof EndpointForm, v: string) => onChange({ ...value, [k]: v });
  const [pickerOpen, setPickerOpen] = useState(false);

  const applyPicked = (addr: PickedAddress) => {
    onChange({
      ...value,
      street1: addr.street1 || '',
      street2: addr.street2 || '',
      city: addr.city || '',
      state: addr.state || '',
      zip_code: addr.zip_code || '',
      country: addr.country || '',
      latitude: typeof addr.latitude === 'number' && addr.latitude !== 0 ? String(addr.latitude) : value.latitude,
      longitude: typeof addr.longitude === 'number' && addr.longitude !== 0 ? String(addr.longitude) : value.longitude,
    });
  };

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          {title}
        </div>
        {MAPS_ENABLED && (
          <>
            <Button type="button" variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
              <MapPin className="h-3.5 w-3.5 mr-1.5" /> Pick on map
            </Button>
            <MapLocationPicker
              open={pickerOpen}
              onOpenChange={setPickerOpen}
              title={`Pick ${title} location`}
              initial={{ latitude: value.latitude, longitude: value.longitude }}
              onPick={applyPicked}
            />
          </>
        )}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor={`${idPrefix}-street1`}>Street Address *</Label>
          <Input id={`${idPrefix}-street1`} placeholder="20 W 34th St" value={value.street1}
            onChange={(e) => set('street1', e.target.value)} />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor={`${idPrefix}-street2`}>Street Address Line 2 (optional)</Label>
          <Input id={`${idPrefix}-street2`} placeholder="Floor 2" value={value.street2}
            onChange={(e) => set('street2', e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-city`}>City *</Label>
          <Input id={`${idPrefix}-city`} placeholder="New York" value={value.city}
            onChange={(e) => set('city', e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-state`}>State *</Label>
          <Input id={`${idPrefix}-state`} placeholder="NY" value={value.state}
            onChange={(e) => set('state', e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-zip`}>Zip Code *</Label>
          <Input id={`${idPrefix}-zip`} placeholder="10001" value={value.zip_code}
            onChange={(e) => set('zip_code', e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-country`}>Country *</Label>
          <Input id={`${idPrefix}-country`} placeholder="US" value={value.country}
            onChange={(e) => set('country', e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-name`}>Contact Name</Label>
          <Input id={`${idPrefix}-name`} placeholder="Jane Doe" value={value.name}
            onChange={(e) => set('name', e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-phone`}>Contact Phone (E.164)</Label>
          <Input id={`${idPrefix}-phone`} placeholder="+15555550123" value={value.phone_number}
            onChange={(e) => set('phone_number', e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-lat`}>Latitude (optional)</Label>
          <Input id={`${idPrefix}-lat`} placeholder="40.7484" value={value.latitude}
            onChange={(e) => set('latitude', e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-lng`}>Longitude (optional)</Label>
          <Input id={`${idPrefix}-lng`} placeholder="-73.9857" value={value.longitude}
            onChange={(e) => set('longitude', e.target.value)} />
        </div>
      </div>
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: number; icon: React.ElementType; className?: string }> = ({
  label, value, icon: Icon, className,
}) => (
  <Card className="border-none shadow-[0px_0px_10px_0px_rgba(0,0,0,0.06)]">
    <CardContent className="flex items-center gap-3 p-4">
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${className || 'bg-slate-100 text-slate-700'}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-2xl font-semibold leading-none">{value}</p>
        <p className="text-xs text-muted-foreground mt-1">{label}</p>
      </div>
    </CardContent>
  </Card>
);

type UberTab = 'connection' | 'webhook' | 'new-delivery' | 'deliveries';

const UberDirectPage: React.FC = () => {
  const { selectedAccountId, isLoading: accountsLoading } = useBusinessAccountContext();
  const { selectedWorkspaceId } = useWorkspaceContext();
  const scopeId = selectedAccountId || selectedWorkspaceId;
  const navigate = useNavigate();

  const { socket, isConnected: socketConnected } = useChat();
  const businessRoomRef = useRef<string | null>(null);
  const [lastWebhookAt, setLastWebhookAt] = useState<number | null>(null);

  const [activeTab, setActiveTab] = useState<UberTab>('connection');

  const [form, setForm] = useState({
    environment: 'sandbox' as UberDirectEnvironment,
    customer_id: '',
    client_id: '',
    client_secret: '',
    webhook_signing_key: '',
    account_label: '',
  });
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState<{
    environment?: UberDirectEnvironment;
    account_label?: string;
    source?: string;
  } | null>(null);

  const [pickup, setPickup] = useState<EndpointForm>(emptyEndpoint());
  const [dropoff, setDropoff] = useState<EndpointForm>(emptyEndpoint());
  const [quoting, setQuoting] = useState(false);
  const [quote, setQuote] = useState<{
    quote_id: string;
    fee: number;
    currency: string;
    dropoff_eta?: string;
    expires?: string;
  } | null>(null);
  const [quoteExpiresAt, setQuoteExpiresAt] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(0);

  const [manifestItems, setManifestItems] = useState<{ name: string; quantity: string; weight: string }[]>([
    { name: '', quantity: '1', weight: '500' },
  ]);
  const [creatingDelivery, setCreatingDelivery] = useState(false);

  const [deliveries, setDeliveries] = useState<DeliveryRow[]>([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [cancelingId, setCancelingId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailMode, setDetailMode] = useState<'delivery' | 'quote'>('delivery');
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detail, setDetail] = useState<UberDeliveryDetail | null>(null);
  const [detailRow, setDetailRow] = useState<DeliveryRow | null>(null);

  const [proofOpen, setProofOpen] = useState(false);
  const [proofLoading, setProofLoading] = useState(false);
  const [proofError, setProofError] = useState<string | null>(null);
  const [proofUnavailable, setProofUnavailable] = useState(false);
  const [proof, setProof] = useState<UberProofOfDelivery | null>(null);
  const [proofDeliveryId, setProofDeliveryId] = useState<string | null>(null);
  const [proofWaypoint, setProofWaypoint] = useState<UberProofWaypoint>('dropoff');
  const [proofType, setProofType] = useState<UberProofType>('picture');

  const resetState = useCallback(() => {
    setIsConnected(false);
    setConnectionInfo(null);
    setQuote(null);
    setQuoteExpiresAt(null);
    setDeliveries([]);
    setPage(0);
    setTotalCount(0);
    setStatusCounts({});
  }, []);

  const checkStatus = useCallback(async () => {
    if (!scopeId) return;
    setLoadingStatus(true);
    const result = await uberDirectApi.getStatus(selectedAccountId, selectedWorkspaceId);
    if (result.ok && result.connected) {
      setIsConnected(true);
      setConnectionInfo({
        environment: result.environment,
        account_label: result.account_label,
        source: result.source,
      });
      setActiveTab((prev) => (prev === 'connection' ? 'deliveries' : prev));
    } else {
      resetState();
      setActiveTab('connection');
    }
    setLoadingStatus(false);
  }, [scopeId, selectedAccountId, selectedWorkspaceId, resetState]);

  useEffect(() => {
    if (scopeId) {
      checkStatus();
    } else {
      resetState();
    }
  }, [scopeId, checkStatus, resetState]);

  useEffect(() => {
    if (!quoteExpiresAt) {
      setSecondsLeft(0);
      return;
    }
    const tick = () => {
      const remaining = Math.max(0, Math.round((quoteExpiresAt - Date.now()) / 1000));
      setSecondsLeft(remaining);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [quoteExpiresAt]);

  const quoteExpired = Boolean(quote) && secondsLeft <= 0;

  const countdownLabel = useMemo(() => {
    if (secondsLeft <= 0) return 'Expired';
    const m = Math.floor(secondsLeft / 60);
    const s = secondsLeft % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }, [secondsLeft]);

  const stats = useMemo(() => {
    let total = 0, active = 0, delivered = 0, closed = 0;
    for (const [rawStatus, rawCount] of Object.entries(statusCounts)) {
      const s = rawStatus.toLowerCase();
      const n = Number(rawCount) || 0;
      total += n;
      if (ACTIVE_STATUSES.has(s)) active += n;
      else if (DELIVERED_STATUSES.has(s)) delivered += n;
      else if (CLOSED_STATUSES.has(s)) closed += n;
    }
    return { total, active, delivered, closed };
  }, [statusCounts]);

  const loadDeliveries = useCallback(async () => {
    if (!scopeId) return;
    setLoadingDeliveries(true);
    const params: { limit: number; offset: number; status?: string } = {
      limit: pageSize,
      offset: page * pageSize,
    };
    if (statusFilter !== 'all') params.status = statusFilter;
    const result = await uberDirectApi.listDeliveries(selectedAccountId, params, selectedWorkspaceId);
    if (result.ok) {
      const items = (result.items || result.data || []) as DeliveryRow[];
      const arr = Array.isArray(items) ? items : [];
      const total = typeof result.total === 'number' ? result.total : arr.length;
      const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);
      if (page > lastPage) {
        setPage(lastPage);
        setLoadingDeliveries(false);
        return;
      }
      setDeliveries(arr);
      setTotalCount(total);
      setStatusCounts(
        result.statusCounts && typeof result.statusCounts === 'object'
          ? result.statusCounts
          : {}
      );
    } else {
      showApiError(result, { fallback: { title: 'Error' } });
    }
    setLoadingDeliveries(false);
  }, [scopeId, selectedAccountId, selectedWorkspaceId, page, pageSize, statusFilter]);

  useEffect(() => {
    setPage(0);
  }, [statusFilter]);

  useEffect(() => {
    if (scopeId && isConnected) {
      loadDeliveries();
    }
  }, [scopeId, isConnected, loadDeliveries]);

  useEffect(() => {
    if (!socket || !socketConnected) {
      businessRoomRef.current = null;
      return;
    }

    const nextRoom = selectedAccountId ? `business-account-${selectedAccountId}` : null;

    if (businessRoomRef.current && businessRoomRef.current !== nextRoom) {
      socket.emit('leave_room', { roomId: businessRoomRef.current });
      businessRoomRef.current = null;
    }

    if (nextRoom && businessRoomRef.current !== nextRoom) {
      socket.emit('join_room', { roomId: nextRoom });
      businessRoomRef.current = nextRoom;
    }

    if (!nextRoom && businessRoomRef.current) {
      socket.emit('leave_room', { roomId: businessRoomRef.current });
      businessRoomRef.current = null;
    }

    return () => {
      if (businessRoomRef.current) {
        socket.emit('leave_room', { roomId: businessRoomRef.current });
        businessRoomRef.current = null;
      }
    };
  }, [socket, socketConnected, selectedAccountId]);

  useEffect(() => {
    if (!socket || !socketConnected) return;

    const handler = (data: {
      delivery_id?: string;
      status?: string;
      complete?: boolean;
      fee?: number | null;
      currency?: string | null;
      tracking_url?: string | null;
      courier_name?: string | null;
      courier_phone?: string | null;
      pickup_eta?: string | null;
      dropoff_eta?: string | null;
      undeliverable_reason?: string | null;
      kind?: 'delivery_status' | 'courier_update' | 'refund_request';
      business_account_id?: string;
      timestamp?: string;
    }) => {
      try {
        if (data?.business_account_id && data.business_account_id !== selectedAccountId) {
          return;
        }
        const pushedId = data?.delivery_id;
        if (!pushedId) return;

        setLastWebhookAt(Date.now());

        const patch: Partial<DeliveryRow> = {};
        if (data.status != null) patch.status = data.status;
        if (data.complete != null) patch.complete = data.complete;
        if (data.fee != null) patch.fee = data.fee;
        if (data.currency != null) patch.currency = data.currency;
        if (data.tracking_url != null) patch.tracking_url = data.tracking_url;
        if (data.dropoff_eta != null) patch.dropoff_eta = data.dropoff_eta;
        if (data.undeliverable_reason != null) patch.undeliverable_reason = data.undeliverable_reason;

        let matched = false;
        setDeliveries((prev) => {
          let didMatch = false;
          const next = prev.map((row) => {
            const rowId = row.delivery_id || row.id;
            if (rowId === pushedId) {
              didMatch = true;
              return { ...row, ...patch };
            }
            return row;
          });
          matched = didMatch;
          return didMatch ? next : prev;
        });

        if (!matched && data.kind === 'delivery_status') {
          loadDeliveries();
        }

        setDetail((prevDetail) => {
          if (!prevDetail) return prevDetail;
          const detailId = prevDetail.id || prevDetail.delivery_id;
          if (detailId !== pushedId) return prevDetail;
          const merged: UberDeliveryDetail = { ...prevDetail, ...patch };
          if (data.courier_name != null || data.courier_phone != null) {
            merged.courier = {
              ...(prevDetail.courier || {}),
              ...(data.courier_name != null ? { name: data.courier_name } : {}),
              ...(data.courier_phone != null ? { phone_number: data.courier_phone } : {}),
            };
          }
          if (data.pickup_eta != null) merged.pickup_eta = data.pickup_eta;
          return merged;
        });
      } catch {
      }
    };

    socket.on('uber_direct:delivery_updated', handler);
    return () => {
      socket.off('uber_direct:delivery_updated', handler);
    };
  }, [socket, socketConnected, selectedAccountId, loadDeliveries]);

  const liveLabel = useMemo(() => {
    if (!lastWebhookAt) return null;
    try {
      return new Date(lastWebhookAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' });
    } catch {
      return null;
    }
  }, [lastWebhookAt]);

  const handleConnect = async () => {
    if (!scopeId || !form.customer_id || !form.client_id || !form.client_secret) return;
    setConnecting(true);
    const result = await uberDirectApi.connect({
      business_account_id: selectedAccountId,
      workspace_id: selectedWorkspaceId,
      environment: form.environment,
      customer_id: form.customer_id.trim(),
      client_id: form.client_id.trim(),
      client_secret: form.client_secret,
      webhook_signing_key: form.webhook_signing_key.trim() || undefined,
      account_label: form.account_label.trim() || undefined,
    });
    if (result.ok) {
      toast({ variant: "success", title: 'Connected', description: 'Uber Direct connected successfully' });
      setForm({
        environment: 'sandbox', customer_id: '', client_id: '', client_secret: '',
        webhook_signing_key: '', account_label: '',
      });
      checkStatus();
    } else {
      showApiError(result, { fallback: { title: 'Connection failed' } });
    }
    setConnecting(false);
  };

  const handleDisconnect = async () => {
    if (!scopeId) return;
    setDisconnecting(true);
    const result = await uberDirectApi.disconnect(selectedAccountId, selectedWorkspaceId);
    if (result.ok) {
      toast({ variant: "success", title: 'Disconnected', description: 'Uber Direct disconnected' });
      setDisconnectDialogOpen(false);
      resetState();
      setActiveTab('connection');
    } else {
      showApiError(result, { fallback: { title: 'Error' } });
    }
    setDisconnecting(false);
  };

  const handleCreateQuote = async () => {
    if (!scopeId) return;
    if (!endpointValid(pickup)) {
      toast({ title: 'Pickup incomplete', description: 'Fill street, city, state, zip and country for pickup.', variant: 'destructive' });
      return;
    }
    if (!endpointValid(dropoff)) {
      toast({ title: 'Dropoff incomplete', description: 'Fill street, city, state, zip and country for dropoff.', variant: 'destructive' });
      return;
    }
    setQuoting(true);
    setQuote(null);
    setQuoteExpiresAt(null);
    const result = await uberDirectApi.createQuote({
      business_account_id: selectedAccountId,
      workspace_id: selectedWorkspaceId,
      pickup: buildEndpoint(pickup),
      dropoff: buildEndpoint(dropoff),
    });
    if (result.ok) {
      setQuote({
        quote_id: result.quote_id || '',
        fee: Number(result.fee ?? 0),
        currency: result.currency || 'USD',
        dropoff_eta: result.dropoff_eta,
        expires: result.expires,
      });
      const expiresMs = result.expires ? new Date(result.expires).getTime() : Date.now() + 15 * 60 * 1000;
      setQuoteExpiresAt(Number.isNaN(expiresMs) ? Date.now() + 15 * 60 * 1000 : expiresMs);
      toast({ variant: "success", title: 'Quote ready', description: `Fee ${formatFee(result.fee, result.currency)}` });
    } else if (result.code === 'NOT_SERVICEABLE') {
      showApiError(result, {
        fallback: {
          title: 'Address not serviceable',
          description:
            'Uber Direct could not locate or service these addresses. Check the street, city, and that Country matches the address (e.g. IN for India), or try a different pickup/dropoff.',
        },
      });
    } else {
      showApiError(result, { fallback: { title: 'Quote failed', description: 'The request was invalid.' } });
    }
    setQuoting(false);
  };

  const addManifestItem = () => setManifestItems((prev) => [...prev, { name: '', quantity: '1', weight: '500' }]);
  const removeManifestItem = (idx: number) =>
    setManifestItems((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));
  const updateManifestItem = (idx: number, k: 'name' | 'quantity' | 'weight', v: string) =>
    setManifestItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [k]: v } : it)));

  const manifestValid = manifestItems.every(
    (it) => it.name.trim() && Number(it.quantity) > 0 && Number(it.weight) > 0
  );

  const handleCreateDelivery = async () => {
    if (!scopeId) return;
    if (!quote || !quote.quote_id) {
      toast({ title: 'No quote', description: 'Create a quote first.', variant: 'destructive' });
      return;
    }
    if (quoteExpired) {
      toast({ title: 'Quote expired', description: 'This quote has expired. Re-quote before dispatching.', variant: 'destructive' });
      return;
    }
    if (!manifestValid) {
      toast({ title: 'Manifest incomplete', description: 'Each item needs a name, quantity and weight (g).', variant: 'destructive' });
      return;
    }
    const items: UberManifestItem[] = manifestItems.map((it) => ({
      name: it.name.trim(),
      quantity: Number(it.quantity),
      weight: Number(it.weight),
    }));
    setCreatingDelivery(true);
    const result = await uberDirectApi.createDelivery({
      business_account_id: selectedAccountId,
      workspace_id: selectedWorkspaceId,
      quote_id: quote.quote_id,
      pickup: buildEndpoint(pickup),
      dropoff: buildEndpoint(dropoff),
      manifest_items: items,
    });
    if (result.ok) {
      toast({ variant: "success", title: 'Delivery created', description: `Status: ${result.status || 'pending'}` });
      setQuote(null);
      setQuoteExpiresAt(null);
      setActiveTab('deliveries');
      loadDeliveries();
    } else if (result.code === 'QUOTE_EXPIRED') {
      toast({ title: 'Quote expired', description: 'The quote expired before dispatch. Please re-quote.', variant: 'destructive' });
      setQuote(null);
      setQuoteExpiresAt(null);
    } else if (result.code === 'UBER_API_ERROR') {
      toast({
        title: 'Status unknown',
        description: 'Uber returned a server error. The delivery may still have been created — refresh the list to reconcile.',
        variant: 'destructive',
      });
      loadDeliveries();
    } else {
      showApiError(result, { fallback: { title: 'Delivery failed' } });
    }
    setCreatingDelivery(false);
  };

  const handleRefreshDelivery = async (row: DeliveryRow) => {
    const id = row.delivery_id || row.id;
    if (!scopeId || !id) return;
    setRefreshingId(id);
    const result = await uberDirectApi.getDelivery(selectedAccountId, id, selectedWorkspaceId);
    if (result.ok) {
      await loadDeliveries();
    } else {
      showApiError(result, {
        fallback: {
          title: 'Status unknown',
          description: 'Could not sync this delivery — try again.',
        },
      });
    }
    setRefreshingId(null);
  };

  const handleCancelDelivery = async (row: DeliveryRow) => {
    const id = row.delivery_id || row.id;
    if (!scopeId || !id) return;
    setCancelingId(id);
    const result = await uberDirectApi.cancelDelivery(selectedAccountId, id, selectedWorkspaceId);
    if (result.ok) {
      toast({ variant: "success", title: 'Canceled', description: 'Delivery canceled' });
      await loadDeliveries();
    } else if (result.code === 'DELIVERY_NOT_CANCELABLE') {
      toast({
        title: 'Cannot cancel',
        description: 'This delivery is past the cancelable stage (courier already picked up).',
        variant: 'destructive',
      });
      await loadDeliveries();
    } else {
      showApiError(result, { fallback: { title: 'Error' } });
    }
    setCancelingId(null);
  };

  const isCancelable = (row: DeliveryRow) => {
    const s = (row.status || '').toLowerCase();
    return !row.complete && !NON_CANCELABLE_STATUSES.has(s) && s !== 'quote';
  };

  const hasProof = (row: DeliveryRow) => PROOF_STATUSES.has((row.status || '').toLowerCase());

  const isQuoteRow = (row: DeliveryRow) =>
    (row.status || '').toLowerCase() === 'quote' || !(row.delivery_id || '').trim();

  const openDetail = async (row: DeliveryRow) => {
    setDetailRow(row);
    setDetailOpen(true);
    setDetailError(null);
    setDetail(null);

    if (isQuoteRow(row)) {
      setDetailMode('quote');
      setDetailLoading(false);
      return;
    }

    setDetailMode('delivery');
    const id = row.delivery_id || row.id;
    if (!scopeId || !id) {
      setDetailError('Could not load this delivery — missing delivery id.');
      setDetailLoading(false);
      return;
    }
    setDetailLoading(true);
    const result = await uberDirectApi.getDelivery(selectedAccountId, id, selectedWorkspaceId);
    if (result.ok) {
      const { ok, error, code, ...rest } = result;
      void ok; void error; void code;
      setDetail(rest as UberDeliveryDetail);
    } else {
      setDetailError(result.error || 'Could not load this delivery. Try again.');
    }
    setDetailLoading(false);
  };

  const closeDetail = (open: boolean) => {
    setDetailOpen(open);
    if (!open) {
      setDetail(null);
      setDetailRow(null);
      setDetailError(null);
      setDetailLoading(false);
      setDetailMode('delivery');
    }
  };

  const fetchProof = useCallback(
    async (id: string, waypoint: UberProofWaypoint, type: UberProofType) => {
      if (!scopeId || !id) return;
      setProofLoading(true);
      setProofError(null);
      setProofUnavailable(false);
      setProof(null);
      const result = await uberDirectApi.getProofOfDelivery(selectedAccountId, id, { waypoint, type }, selectedWorkspaceId);
      if (result.ok) {
        const { ok, error, code, ...rest } = result;
        void ok; void error; void code;
        setProof(rest as UberProofOfDelivery);
      } else if (
        result.code === 'PROOF_NOT_AVAILABLE' ||
        /proof|not found/i.test(result.error || '')
      ) {
        setProofUnavailable(true);
      } else {
        setProofError(result.error || 'Could not load proof of delivery. Try again.');
      }
      setProofLoading(false);
    },
    [scopeId, selectedAccountId, selectedWorkspaceId]
  );

  const openProof = (row: DeliveryRow) => {
    const id = row.delivery_id || row.id;
    if (!scopeId || !id) return;
    setProofWaypoint('dropoff');
    setProofType('picture');
    setProofDeliveryId(id);
    setProofOpen(true);
  };

  useEffect(() => {
    if (proofOpen && proofDeliveryId) {
      fetchProof(proofDeliveryId, proofWaypoint, proofType);
    }
  }, [proofOpen, proofDeliveryId, proofWaypoint, proofType, fetchProof]);

  const closeProof = (open: boolean) => {
    setProofOpen(open);
    if (!open) {
      setProof(null);
      setProofError(null);
      setProofUnavailable(false);
      setProofLoading(false);
      setProofDeliveryId(null);
    }
  };

  const webhookUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/webhooks/uber-direct`;

  const copyWebhookUrl = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(webhookUrl);
        toast({ variant: "success", title: 'Copied', description: 'Webhook URL copied to clipboard' });
        return;
      }
    } catch {
    }
    const el = document.getElementById('uber-webhook-url') as HTMLInputElement | null;
    if (el) {
      el.focus();
      el.select();
    }
    toast({ variant: "success", title: 'Copied', description: 'Webhook URL selected — press Ctrl/Cmd+C to copy' });
  };

  if (accountsLoading || loadingStatus) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/home/integrations')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <CardTitle className="text-xl sm:text-2xl">Uber Direct</CardTitle>
          <CardDescription className="text-sm">On-demand last-mile courier delivery</CardDescription>
        </div>
        {isConnected && (
          <Badge variant="outline" className="flex items-center gap-1 border-0 bg-green-100 text-green-700">
            <CheckCircle className="h-3.5 w-3.5" /> Connected
          </Badge>
        )}
        {!isConnected && (
          <Badge variant="outline" className="flex items-center gap-1 border-0 bg-slate-100 text-slate-600">
            <XCircle className="h-3.5 w-3.5" /> Not connected
          </Badge>
        )}
      </div>

      {!selectedAccountId ? (
        <Card className="border-none shadow-[0px_0px_10px_0px_rgba(0,0,0,0.06)]">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Truck className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Select a business account</h3>
            <p className="text-sm text-muted-foreground">
              Choose a business account from the header to manage Uber Direct deliveries.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as UberTab)} className="space-y-6">
          <AnimatedTabs
            activeTab={activeTab}
            onTabChange={(v) => setActiveTab(v as UberTab)}
            layoutId="uber-direct-tab-pill"
            tabsListClassName="mb-0"
            tabs={[
              { label: 'Connection', value: 'connection', icon: <PlugZap className="h-4 w-4" /> },
              { label: 'Webhook', value: 'webhook', disabled: !isConnected, icon: isConnected ? <Radio className="h-4 w-4" /> : <Lock className="h-4 w-4" /> },
              { label: 'New Delivery', value: 'new-delivery', disabled: !isConnected, icon: isConnected ? <Plus className="h-4 w-4" /> : <Lock className="h-4 w-4" /> },
              { label: 'Deliveries', value: 'deliveries', disabled: !isConnected, icon: isConnected ? <List className="h-4 w-4" /> : <Lock className="h-4 w-4" /> },
            ]}
          />

          <TabsContent value="connection" className="space-y-6">
          <Card className="border-none shadow-[0px_0px_10px_0px_rgba(0,0,0,0.06)]">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{isConnected ? 'Connection' : 'Connect Uber Direct'}</CardTitle>
                  <CardDescription>
                    {isConnected ? 'Your Uber Direct account is connected for this business account.' : 'Enter your Uber Direct API credentials from the Uber developer portal.'}
                  </CardDescription>
                </div>
                {isConnected && (
                  <Button variant="outline" size="sm" onClick={checkStatus} disabled={loadingStatus}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${loadingStatus ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isConnected ? (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm font-medium">Connected</span>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Environment</Label>
                      <p className="font-medium capitalize">{connectionInfo?.environment || 'sandbox'}</p>
                    </div>
                    {connectionInfo?.account_label && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Account Label</Label>
                        <p className="font-medium">{connectionInfo.account_label}</p>
                      </div>
                    )}
                    {connectionInfo?.source && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Credential Source</Label>
                        <p className="font-medium capitalize">{connectionInfo.source.replace('_', ' ')}</p>
                      </div>
                    )}
                  </div>
                  <Button variant="destructive" onClick={() => setDisconnectDialogOpen(true)} disabled={disconnecting}>
                    {disconnecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Unplug className="h-4 w-4 mr-2" />}
                    Disconnect
                  </Button>

                  <DisconnectConfirmDialog
                    open={disconnectDialogOpen}
                    onOpenChange={setDisconnectDialogOpen}
                    onConfirm={handleDisconnect}
                    isLoading={disconnecting}
                    integration="uberdirect"
                  />
                </motion.div>
              ) : (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="environment">Environment</Label>
                    <Select value={form.environment} onValueChange={(val) => setForm((p) => ({ ...p, environment: val as UberDirectEnvironment }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sandbox">Sandbox (Testing)</SelectItem>
                        <SelectItem value="production">Production (Live)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customer_id">Customer ID *</Label>
                    <Input id="customer_id" placeholder="Your Uber Direct customer ID" value={form.customer_id}
                      onChange={(e) => setForm((p) => ({ ...p, customer_id: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client_id">Client ID *</Label>
                    <Input id="client_id" placeholder="Your Uber app client ID" value={form.client_id}
                      onChange={(e) => setForm((p) => ({ ...p, client_id: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client_secret">Client Secret *</Label>
                    <Input id="client_secret" type="password" placeholder="Your Uber app client secret" value={form.client_secret}
                      onChange={(e) => setForm((p) => ({ ...p, client_secret: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="webhook_signing_key">Webhook Signing Key (optional)</Label>
                    <Input id="webhook_signing_key" type="password" placeholder="For webhook signature verification" value={form.webhook_signing_key}
                      onChange={(e) => setForm((p) => ({ ...p, webhook_signing_key: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="account_label">Account Label (optional)</Label>
                    <Input id="account_label" placeholder="A friendly name for this connection" value={form.account_label}
                      onChange={(e) => setForm((p) => ({ ...p, account_label: e.target.value }))} />
                  </div>
                  <Button onClick={handleConnect} className="w-full"
                    disabled={connecting || !form.customer_id || !form.client_id || !form.client_secret}>
                    {connecting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Testing &amp; Saving...</> : <><PlugZap className="h-4 w-4 mr-2" />Test &amp; Save</>}
                  </Button>
                </motion.div>
              )}
            </CardContent>
          </Card>
          </TabsContent>

          <TabsContent value="webhook" className="space-y-6">
          {!isConnected ? (
            <Card className="border-none shadow-[0px_0px_10px_0px_rgba(0,0,0,0.06)]">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <Lock className="h-10 w-10 text-muted-foreground mb-3" />
                <h3 className="text-base font-semibold mb-1">Connect Uber Direct first</h3>
                <p className="text-sm text-muted-foreground">Webhook setup is available once a connection is configured.</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-none shadow-[0px_0px_10px_0px_rgba(0,0,0,0.06)]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Webhook className="h-4 w-4 text-muted-foreground" /> Webhooks
                </CardTitle>
                <CardDescription>
                  Register this URL in the Uber dev portal → Webhooks. Use your public/ngrok host, not localhost.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Webhooks push live delivery status updates back into wenext as the courier progresses.
                  Sandbox (robo-courier) deliveries advance automatically without any driver, so their statuses
                  update on their own once the webhook is registered.
                </p>
                <Label htmlFor="uber-webhook-url" className="text-xs text-muted-foreground">Webhook endpoint</Label>
                <div className="flex items-center gap-2">
                  <Input id="uber-webhook-url" readOnly value={webhookUrl} className="font-mono text-xs" />
                  <Button variant="outline" size="sm" onClick={copyWebhookUrl}>
                    <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Register this endpoint in the Uber developer portal under the <strong>same environment</strong>
                  {' '}(testing vs production) as the deliveries you create here. Use your public / ngrok host —
                  {' '}<strong>not</strong> localhost — so Uber can reach it.
                </p>
                <p className="text-xs text-muted-foreground">
                  Signature verification is enforced server-side using your Webhook Signing Key.
                </p>
              </CardContent>
            </Card>
          )}
          </TabsContent>

          <TabsContent value="new-delivery" className="space-y-6">
          {!isConnected ? (
            <Card className="border-none shadow-[0px_0px_10px_0px_rgba(0,0,0,0.06)]">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <Lock className="h-10 w-10 text-muted-foreground mb-3" />
                <h3 className="text-base font-semibold mb-1">Connect Uber Direct first</h3>
                <p className="text-sm text-muted-foreground">Quotes and deliveries are available once a connection is configured.</p>
              </CardContent>
            </Card>
          ) : (
            <>
            <Card className="border-none shadow-[0px_0px_10px_0px_rgba(0,0,0,0.06)]">
              <CardHeader>
                <CardTitle>Create Quote</CardTitle>
                <CardDescription>Get a delivery fee and ETA. Quotes are valid for 15 minutes.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <EndpointFields title="Pickup" value={pickup} onChange={setPickup} idPrefix="pickup" />
                  <EndpointFields title="Dropoff" value={dropoff} onChange={setDropoff} idPrefix="dropoff" />
                </div>
                <Button onClick={handleCreateQuote} disabled={quoting}>
                  {quoting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Getting quote...</> : <><Truck className="h-4 w-4 mr-2" />Get Quote</>}
                </Button>

                {quote && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className="rounded-lg border p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                      <div>
                        <Label className="text-xs text-muted-foreground">Fee</Label>
                        <p className="font-semibold text-base">{formatFee(quote.fee, quote.currency)}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Dropoff ETA</Label>
                        <p className="font-medium text-sm">{formatDateTime(quote.dropoff_eta)}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Quote ID</Label>
                        <p className="font-mono text-xs break-all">{quote.quote_id}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Expires in</Label>
                        <p className={`font-semibold text-sm flex items-center gap-1 ${quoteExpired ? 'text-red-600' : 'text-foreground'}`}>
                          <Clock className="h-3.5 w-3.5" />{countdownLabel}
                        </p>
                      </div>
                    </div>
                    {quoteExpired && (
                      <div className="flex items-center justify-between rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                        <span>This quote expired. Re-quote before dispatching.</span>
                        <Button size="sm" variant="outline" onClick={handleCreateQuote} disabled={quoting}>
                          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Re-quote
                        </Button>
                      </div>
                    )}
                  </motion.div>
                )}
              </CardContent>
            </Card>

            {quote && !quoteExpired && (
            <Card className="border-none shadow-[0px_0px_10px_0px_rgba(0,0,0,0.06)]">
              <CardHeader>
                <CardTitle>Create Delivery</CardTitle>
                <CardDescription>Dispatch a courier using quote <span className="font-mono text-xs">{quote.quote_id}</span>.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <Label>Manifest Items</Label>
                  {manifestItems.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-1 gap-2 sm:grid-cols-12 sm:items-end">
                      <div className="space-y-1.5 sm:col-span-6">
                        <Label htmlFor={`mi-name-${idx}`} className="text-xs text-muted-foreground">Name</Label>
                        <Input id={`mi-name-${idx}`} placeholder="Item name" value={item.name}
                          onChange={(e) => updateManifestItem(idx, 'name', e.target.value)} />
                      </div>
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label htmlFor={`mi-qty-${idx}`} className="text-xs text-muted-foreground">Qty</Label>
                        <Input id={`mi-qty-${idx}`} type="number" min={1} value={item.quantity}
                          onChange={(e) => updateManifestItem(idx, 'quantity', e.target.value)} />
                      </div>
                      <div className="space-y-1.5 sm:col-span-3">
                        <Label htmlFor={`mi-wt-${idx}`} className="text-xs text-muted-foreground">Weight (g)</Label>
                        <Input id={`mi-wt-${idx}`} type="number" min={1} value={item.weight}
                          onChange={(e) => updateManifestItem(idx, 'weight', e.target.value)} />
                      </div>
                      <div className="sm:col-span-1">
                        <Button variant="ghost" size="icon" onClick={() => removeManifestItem(idx)}
                          disabled={manifestItems.length <= 1} title="Remove item">
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addManifestItem}>
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add Item
                  </Button>
                </div>
                <Button onClick={handleCreateDelivery} disabled={creatingDelivery || !manifestValid}>
                  {creatingDelivery ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Dispatching...</> : <><Truck className="h-4 w-4 mr-2" />Create Delivery</>}
                </Button>
              </CardContent>
            </Card>
            )}
            </>
          )}
          </TabsContent>

          <TabsContent value="deliveries" className="space-y-6">
          {!isConnected ? (
            <Card className="border-none shadow-[0px_0px_10px_0px_rgba(0,0,0,0.06)]">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <Lock className="h-10 w-10 text-muted-foreground mb-3" />
                <h3 className="text-base font-semibold mb-1">Connect Uber Direct first</h3>
                <p className="text-sm text-muted-foreground">Your deliveries appear here once a connection is configured.</p>
              </CardContent>
            </Card>
          ) : (
            <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatCard label="Total" value={stats.total} icon={Package} className="bg-slate-100 text-slate-700" />
              <StatCard label="Active" value={stats.active} icon={Truck} className="bg-blue-100 text-blue-700" />
              <StatCard label="Delivered" value={stats.delivered} icon={CheckCircle} className="bg-green-100 text-green-700" />
              <StatCard label="Canceled / Returned" value={stats.closed} icon={XCircle} className="bg-red-100 text-red-700" />
            </div>

            <Card className="border-none shadow-[0px_0px_10px_0px_rgba(0,0,0,0.06)]">
              <CardHeader>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      Deliveries
                      {liveLabel && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                          <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
                          Live · last update {liveLabel}
                        </span>
                      )}
                    </CardTitle>
                    <CardDescription>Recent Uber Direct deliveries for this business account.</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-[160px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_FILTER_OPTIONS.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s === 'all' ? 'All statuses' : s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={loadDeliveries} disabled={loadingDeliveries}>
                      <RefreshCw className={`h-4 w-4 mr-2 ${loadingDeliveries ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingDeliveries ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : deliveries.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Truck className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No deliveries yet</h3>
                    <p className="text-sm text-muted-foreground">
                      {statusFilter === 'all'
                        ? 'Create a quote then a delivery to see it here.'
                        : `No deliveries with status "${statusFilter.replace(/_/g, ' ')}".`}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {deliveries.map((row) => {
                      const id = row.delivery_id || row.id || '';
                      return (
                        <div
                          key={id}
                          role="button"
                          tabIndex={0}
                          onClick={() => openDetail(row)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openDetail(row);
                            }
                          }}
                          className="flex cursor-pointer flex-col gap-3 rounded-lg border p-4 transition-colors hover:bg-muted/40 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div className="space-y-1.5">
                            <div className="flex flex-wrap items-center gap-2">
                              {getStatusBadge(row.status)}
                              <span className="font-mono text-xs text-muted-foreground break-all">{id}</span>
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {row.dropoff_name ? <span>To: {row.dropoff_name} · </span> : null}
                              <span>{formatFee(row.fee, row.currency)}</span>
                              {row.dropoff_eta ? <span> · ETA {formatDateTime(row.dropoff_eta)}</span> : null}
                            </div>
                            {row.undeliverable_reason && (
                              <div className="text-xs text-orange-600">Undeliverable: {row.undeliverable_reason.replace(/_/g, ' ')}</div>
                            )}
                            {row.tracking_url && (
                              <a href={row.tracking_url} target="_blank" rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
                                Track delivery <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); openDetail(row); }}>
                              <Eye className="h-3.5 w-3.5 mr-1" /> Details
                            </Button>
                            {hasProof(row) && (
                              <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); openProof(row); }}>
                                <FileCheck className="h-3.5 w-3.5 mr-1" /> Proof
                              </Button>
                            )}
                            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleRefreshDelivery(row); }}
                              disabled={refreshingId === id}>
                              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshingId === id ? 'animate-spin' : ''}`} />
                              Refresh
                            </Button>
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleCancelDelivery(row); }}
                              disabled={!isCancelable(row) || cancelingId === id}
                              className="text-red-500 hover:bg-red-50 hover:text-red-600">
                              {cancelingId === id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Ban className="h-3.5 w-3.5 mr-1" />}
                              Cancel
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {!loadingDeliveries && totalCount > 0 && (
                  <PaginationFooter
                    totalItems={totalCount}
                    currentPage={page + 1}
                    pageSize={pageSize}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    itemNoun="deliveries"
                    onPageChange={(p) => setPage(p - 1)}
                    onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
                  />
                )}
              </CardContent>
            </Card>
            </>
          )}
          </TabsContent>
        </Tabs>
      )}

      <Dialog open={detailOpen} onOpenChange={closeDetail}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{detailMode === 'quote' ? 'Quote Details' : 'Delivery Details'}</DialogTitle>
            <DialogDescription>
              {detailMode === 'quote'
                ? 'Estimated fee and route for this quote. A quote has no courier until dispatched.'
                : 'Live status, tracking and courier information from Uber Direct.'}
            </DialogDescription>
          </DialogHeader>

          {detailMode === 'quote' ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <div className="mt-1">{getStatusBadge(detailRow?.status || 'quote')}</div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Fee</Label>
                  <p className="font-medium">{formatFee(detailRow?.fee, detailRow?.currency)}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Quote ID</Label>
                  <p className="font-mono text-xs break-all">{detailRow?.quote_id || detailRow?.id || '—'}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Dropoff ETA</Label>
                  <p className="text-sm">{formatDateTime(detailRow?.dropoff_eta)}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Created</Label>
                  <p className="text-sm">{formatDateTime(detailRow?.created_at)}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-lg border p-3">
                  <div className="flex items-center gap-2 text-sm font-semibold mb-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" /> Pickup
                  </div>
                  {detailRow?.pickup_name && <p className="text-sm font-medium">{detailRow.pickup_name}</p>}
                  <p className="text-xs text-muted-foreground">{parseStoredAddress(detailRow?.pickup_address)}</p>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="flex items-center gap-2 text-sm font-semibold mb-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" /> Dropoff
                  </div>
                  {detailRow?.dropoff_name && <p className="text-sm font-medium">{detailRow.dropoff_name}</p>}
                  <p className="text-xs text-muted-foreground">{parseStoredAddress(detailRow?.dropoff_address)}</p>
                </div>
              </div>

              <div className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                This is a saved quote. Dispatch it from the New Delivery tab to assign a courier and enable live tracking.
              </div>
            </div>
          ) : detailLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : detailError && !detail ? (
            <div className="space-y-4">
              <div className="rounded-md bg-red-50 px-4 py-4 text-center text-sm text-red-700">{detailError}</div>
              {detailRow && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Status</Label>
                      <div className="mt-1">{getStatusBadge(detailRow.status)}</div>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Fee</Label>
                      <p className="font-medium">{formatFee(detailRow.fee, detailRow.currency)}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Delivery ID</Label>
                      <p className="font-mono text-xs break-all">{detailRow.delivery_id || detailRow.id || '—'}</p>
                    </div>
                  </div>
                  {detailRow.undeliverable_reason && (
                    <div className="rounded-md bg-orange-50 px-3 py-2 text-sm text-orange-700">
                      Undeliverable: {detailRow.undeliverable_reason.replace(/_/g, ' ')}
                    </div>
                  )}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="rounded-lg border p-3">
                      <div className="flex items-center gap-2 text-sm font-semibold mb-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" /> Pickup
                      </div>
                      {detailRow.pickup_name && <p className="text-sm font-medium">{detailRow.pickup_name}</p>}
                      <p className="text-xs text-muted-foreground">{parseStoredAddress(detailRow.pickup_address)}</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <div className="flex items-center gap-2 text-sm font-semibold mb-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" /> Dropoff
                      </div>
                      {detailRow.dropoff_name && <p className="text-sm font-medium">{detailRow.dropoff_name}</p>}
                      <p className="text-xs text-muted-foreground">{parseStoredAddress(detailRow.dropoff_address)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : detail ? (
            <div className="space-y-4">
              <div className="rounded-lg border p-3">
                <DeliveryTimeline status={detail.status} />
              </div>

              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <div className="mt-1">{getStatusBadge(detail.status)}</div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Fee</Label>
                  <p className="font-medium">{formatFee(detail.fee, detail.currency)}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Delivery ID</Label>
                  <p className="font-mono text-xs break-all">{detail.id || detail.delivery_id || '—'}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Pickup ETA</Label>
                  <p className="text-sm">{formatDateTime(detail.pickup_eta)}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Dropoff ETA</Label>
                  <p className="text-sm">{formatDateTime(detail.dropoff_eta)}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Created</Label>
                  <p className="text-sm">{formatDateTime(detail.created)}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Updated</Label>
                  <p className="text-sm">{formatDateTime(detail.updated)}</p>
                </div>
              </div>

              {detail.undeliverable_reason && (
                <div className="rounded-md bg-orange-50 px-3 py-2 text-sm text-orange-700">
                  Undeliverable: {detail.undeliverable_reason.replace(/_/g, ' ')}
                </div>
              )}

              {detail.tracking_url && (
                <div className="rounded-lg border p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    <Navigation className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <div>
                      <div className="text-sm font-semibold">Uber live map</div>
                      <p className="text-xs text-muted-foreground">
                        See the courier&apos;s real-time location and route on Uber&apos;s tracking page.
                        Live delivery status is shown in the timeline above.
                      </p>
                    </div>
                  </div>
                  <Button asChild variant="outline" size="sm" className="shrink-0">
                    <a href={detail.tracking_url} target="_blank" rel="noopener noreferrer">
                      Open live tracking <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
                    </a>
                  </Button>
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-lg border p-3">
                  <div className="flex items-center gap-2 text-sm font-semibold mb-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" /> Pickup
                  </div>
                  <p className="text-sm font-medium">{detail.pickup?.name || '—'}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatWaypointAddress(detail.pickup?.address, detail.pickup?.detailed_address)}
                  </p>
                  {formatLatLng(detail.pickup?.location) && (
                    <p className="mt-1 font-mono text-[11px] text-muted-foreground">{formatLatLng(detail.pickup?.location)}</p>
                  )}
                </div>
                <div className="rounded-lg border p-3">
                  <div className="flex items-center gap-2 text-sm font-semibold mb-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" /> Dropoff
                  </div>
                  <p className="text-sm font-medium">{detail.dropoff?.name || '—'}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatWaypointAddress(detail.dropoff?.address, detail.dropoff?.detailed_address)}
                  </p>
                  {formatLatLng(detail.dropoff?.location) && (
                    <p className="mt-1 font-mono text-[11px] text-muted-foreground">{formatLatLng(detail.dropoff?.location)}</p>
                  )}
                </div>
              </div>

              <div className="rounded-lg border p-3 space-y-1.5">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <User className="h-4 w-4 text-muted-foreground" /> Courier
                </div>
                {detail.courier && (detail.courier.name || detail.courier.phone_number || formatCourierVehicle(detail.courier)) ? (
                  <>
                    {detail.courier.name && <p className="text-sm">{detail.courier.name}</p>}
                    {detail.courier.phone_number && (
                      <p className="text-sm flex items-center gap-1 text-muted-foreground">
                        <Phone className="h-3.5 w-3.5" /> {detail.courier.phone_number}
                      </p>
                    )}
                    {formatCourierVehicle(detail.courier) && (
                      <p className="text-xs text-muted-foreground">{formatCourierVehicle(detail.courier)}</p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">Courier not yet assigned</p>
                )}
              </div>

              {Array.isArray(detail.manifest_items) && detail.manifest_items.length > 0 && (
                <div className="rounded-lg border p-3">
                  <div className="flex items-center gap-2 text-sm font-semibold mb-2">
                    <Package className="h-4 w-4 text-muted-foreground" /> Manifest Items
                  </div>
                  <ul className="space-y-1">
                    {detail.manifest_items.map((it, i) => (
                      <li key={i} className="text-sm text-muted-foreground">
                        {it.name || 'Item'}
                        {it.quantity != null ? ` · ×${it.quantity}` : ''}
                        {it.weight != null ? ` · ${it.weight}g` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={proofOpen} onOpenChange={closeProof}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Proof of Delivery</DialogTitle>
            <DialogDescription>Signature, photo, barcodes and PIN captured at dropoff.</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Waypoint</Label>
              <Select value={proofWaypoint} onValueChange={(v) => setProofWaypoint(v as UberProofWaypoint)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dropoff">Dropoff</SelectItem>
                  <SelectItem value="pickup">Pickup</SelectItem>
                  <SelectItem value="return">Return</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Type</Label>
              <Select value={proofType} onValueChange={(v) => setProofType(v as UberProofType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="picture">Photo</SelectItem>
                  <SelectItem value="signature">Signature</SelectItem>
                  <SelectItem value="pincode">PIN</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {proofLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : proofUnavailable ? (
            <div className="rounded-md bg-muted/50 px-4 py-6 text-center text-sm text-muted-foreground">
              No proof available for this delivery yet. Sandbox (robo-courier) deliveries don&apos;t generate real
              proof — this appears only on live courier deliveries.
            </div>
          ) : proofError ? (
            <div className="rounded-md bg-red-50 px-4 py-6 text-center text-sm text-red-700">{proofError}</div>
          ) : proof ? (
            <div className="space-y-4">
              {typeof proof.proof?.document === 'string' && proof.proof.document.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground">
                    {proofType === 'signature' ? 'Signature' : 'Delivery Photo'}
                  </Label>
                  <img
                    src={
                      proof.proof.document.startsWith('data:')
                        ? proof.proof.document
                        : `data:image/png;base64,${proof.proof.document}`
                    }
                    alt={proofType === 'signature' ? 'Signature' : 'Delivery photo'}
                    className="mt-1 max-h-64 w-full rounded-md border bg-white object-contain"
                  />
                </div>
              )}
              {proofType === 'pincode' && !proof.proof?.document &&
                (proof.proof?.code || proof.proof?.status) && (
                  <div>
                    <Label className="text-xs text-muted-foreground">PIN</Label>
                    <p className="text-sm text-muted-foreground">
                      {proof.proof?.code ? `Code: ${proof.proof.code}` : ''}
                      {proof.proof?.status ? `${proof.proof?.code ? ' · ' : ''}${proof.proof.status}` : ''}
                    </p>
                  </div>
                )}
              {proof.signature?.image_url && (
                <div>
                  <Label className="text-xs text-muted-foreground">Signature{proof.signature.signer_name ? ` — ${proof.signature.signer_name}` : ''}</Label>
                  <img src={proof.signature.image_url} alt="Signature" className="mt-1 max-h-40 rounded-md border bg-white object-contain" />
                </div>
              )}
              {proof.picture && (
                <div>
                  <Label className="text-xs text-muted-foreground">Delivery Photo</Label>
                  <img src={proof.picture} alt="Delivery photo" className="mt-1 max-h-64 w-full rounded-md border object-contain" />
                </div>
              )}
              {Array.isArray(proof.barcodes) && proof.barcodes.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground">Barcodes</Label>
                  <ul className="mt-1 space-y-1">
                    {proof.barcodes.map((b, i) => (
                      <li key={i} className="text-sm text-muted-foreground">
                        <span className="font-mono">{b.value || '—'}</span>
                        {b.type ? ` · ${b.type}` : ''}
                        {b.scan_result ? ` · ${b.scan_result}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {proof.pin_code && (proof.pin_code.entered || proof.pin_code.status) && (
                <div>
                  <Label className="text-xs text-muted-foreground">PIN</Label>
                  <p className="text-sm text-muted-foreground">
                    {proof.pin_code.entered ? `Entered: ${proof.pin_code.entered}` : ''}
                    {proof.pin_code.status ? ` · ${proof.pin_code.status}` : ''}
                  </p>
                </div>
              )}
              {!(typeof proof.proof?.document === 'string' && proof.proof.document.length > 0) &&
                !(proofType === 'pincode' && (proof.proof?.code || proof.proof?.status)) &&
                !proof.signature?.image_url && !proof.picture &&
                !(Array.isArray(proof.barcodes) && proof.barcodes.length > 0) &&
                !(proof.pin_code && (proof.pin_code.entered || proof.pin_code.status)) && (
                  <div className="rounded-md bg-muted/50 px-4 py-6 text-center text-sm text-muted-foreground">
                    Proof not available yet for this delivery.
                  </div>
                )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UberDirectPage;
